import { ProductName } from '@pypestream/api-services';
import { logos } from '@pypestream/design-system';
import type { FeatureFlagsType } from '@pypestream/feature-flags';

import {
  ManagerContext,
  Project,
  ProjectNew,
} from '../xstate/manager.xstate.types';

export type Product = {
  name: ProductName;
  label: string;
  logo: keyof typeof logos;
  productId: string;
  disabled: boolean;
  url?: string;
  index?: number;
  conditional?: boolean;
  integrated?: boolean;
};

export interface GetProductsProps {
  ctx: ManagerContext;
  project?: Project | ProjectNew;
  isNewProjects?: boolean;
}

interface GetProductsResult {
  products: Product[];
  conditionalProducts: Product[];
  selectedProjectURL: string | undefined;
  hasAvailableProduct: boolean;
}

export interface GetProducts {
  (props: GetProductsProps): GetProductsResult;
}

/**
 * Every tool Manager knows about, in display order.
 *
 * One entry per tool: the label, logo and cross-app URL used to live in three
 * separate places keyed on `ProductName`, so registering a tool meant three
 * edits and missing one produced a card with the wrong logo or a dead link.
 *
 * `projectUrl` is a function rather than a string because both the env vars
 * and the localhost check have to be read when the link is built, not when
 * this module is first evaluated.
 */
interface ToolDefinition {
  name: ProductName;
  label: string;
  logo: keyof typeof logos;
  /** Switched on per project, rather than present on every one. */
  conditional?: boolean;
  projectUrl: (projectId: string) => string | undefined;
}

/**
 * Where a tool lives, or undefined when this build was never told.
 *
 * Returning undefined rather than interpolating the missing value is the whole point:
 * `${undefined}/${id}` and `${''}/${id}` are both RELATIVE urls, so the browser resolves them
 * against Manager and the card silently navigates back to Manager instead of failing visibly.
 * `ProductCTA` renders a card with no href as a non-link (it passes `href: url` to `Card`).
 */
const reportedMissingToolUrls = new Set<string>();

const toolOrigin = (
  injected: string | undefined,
  envVar: string,
  localPort?: number
): string | undefined => {
  if (
    localPort &&
    typeof window !== 'undefined' &&
    window.location.hostname.includes('localhost')
  ) {
    return `http://localhost:${localPort}`;
  }

  if (!injected) {
    // The dead card is otherwise silent; this is the only clue a deploy forgot the variable.
    if (!reportedMissingToolUrls.has(envVar)) {
      reportedMissingToolUrls.add(envVar);
      console.error(
        `[manager] ${envVar} was not set when this build ran, so its tool card has no link. Add it to the Manager build_env of this environment's deploy workflow.`
      );
    }
    return undefined;
  }

  // A trailing slash would build `//`, which is a protocol-relative url, not a path.
  return injected.replace(/\/+$/, '');
};

const toolLink = (origin: string | undefined, path: string) =>
  origin === undefined ? undefined : `${origin}${path}`;

const TOOL_DEFINITIONS: ToolDefinition[] = [
  {
    name: ProductName.Organization,
    label: 'Manager',
    logo: 'manager',
    projectUrl: () => '/',
  },
  {
    name: ProductName.AgentAssist,
    label: 'Contact Center',
    logo: 'contactCenter',
    conditional: true,
    projectUrl: (projectId) =>
      toolLink(
        toolOrigin(import.meta.env.FE_AGENT_ASSIST_URL, 'FE_AGENT_ASSIST_URL'),
        `/app/accounts/${projectId}/dashboard`
      ),
  },
  {
    name: ProductName.Analytics,
    label: 'Analytics',
    logo: 'analytics',
    conditional: true,
    projectUrl: (projectId) =>
      toolLink(
        toolOrigin(import.meta.env.FE_ANALYTICS_URL, 'FE_ANALYTICS_URL'),
        `/api/projects/${projectId}/change`
      ),
  },
  {
    name: ProductName.ProStudio,
    label: 'Pro Studio',
    logo: 'proStudio',
    conditional: true,
    projectUrl: (projectId) =>
      toolLink(
        toolOrigin(
          import.meta.env.FE_PRO_STUDIO_URL,
          'FE_PRO_STUDIO_URL',
          5174
        ),
        `/${projectId}`
      ),
  },
  {
    name: ProductName.MaestroStudio,
    label: 'Maestro Studio',
    logo: 'maestro',
    conditional: true,
    projectUrl: (projectId) =>
      toolLink(
        toolOrigin(
          import.meta.env.FE_MAESTRO_STUDIO_URL,
          'FE_MAESTRO_STUDIO_URL',
          5175
        ),
        `/${projectId}`
      ),
  },
];

/**
 * Tools that only exist once their flag is on.
 *
 * Gating here rather than at each of the four places tools are rendered --
 * the dashboard, project details, the create-project modal and the project
 * dropdown all derive from this module, so one entry covers them.
 */
const TOOL_FLAGS: Partial<Record<ProductName, keyof FeatureFlagsType>> = {
  [ProductName.MaestroStudio]: 'maestro-studio',
};

export function isToolReleased(
  name: ProductName,
  featureFlags?: FeatureFlagsType
): boolean {
  const flag = TOOL_FLAGS[name];
  return flag === undefined || Boolean(featureFlags?.[flag]);
}

/**
 * Carries every tool, flagged or not. `ctx.tools` is derived from this list --
 * `allToolProducts` enriches it in place rather than rebuilding it -- so a
 * tool missing here is missing everywhere, whatever its flag says. The gate is
 * `useReleasedTools`, at render, which also hides flagged tools while the
 * flags are still loading.
 */
export const initialTools: Product[] = TOOL_DEFINITIONS.map(
  ({ name, label, logo, conditional }, position) => ({
    name,
    label,
    logo,
    ...(conditional && { conditional }),
    url: undefined,
    disabled: true,
    index: position + 1,
    productId: '',
  })
);

export const getProductUrl = (
  product: ProductName,
  projectId: string
): string | undefined => {
  const tool = TOOL_DEFINITIONS.find((t) => t.name === product);

  // An unregistered product is a programming error, not a missing host: keep the old SPA root.
  return tool ? tool.projectUrl(projectId) : '/';
};

/**
 * href for a Project-Details tool card (PSTUDIO-2541). Returns the external cross-app URL when the
 * tool is enabled, else undefined so the card renders as a non-link. Organization/Manager is
 * excluded (getProductUrl returns the SPA root '/', which is not a "tool instance" link here).
 */
export const productToolHref = (
  product: ProductName,
  projectId: string,
  enabled: boolean
): string | undefined => {
  if (!enabled || product === ProductName.Organization) return undefined;
  return getProductUrl(product, projectId);
};

export const getProducts: GetProducts = ({
  project,
  ctx: {
    userInfo,
    allProducts,
    projects: projectsOld,
    projectsNew,
    routes,
    userProjectIds,
    selectedOrgId,
  },
  isNewProjects = false,
}) => {
  const projects = isNewProjects ? projectsNew : projectsOld;

  const initialProjects = isNewProjects
    ? projectsNew
    : projects?.filter((p) =>
        userProjectIds.includes((p as Project).projectId)
      );

  const transformCtxValues = () => ({
    productIds: allProducts?.reduce(
      (acc, { name, id }) => ({ ...acc, [`${name}`]: id }),
      {} as Record<ProductName, string>
    ),
    enabledTools: [
      ...new Set(
        initialProjects
          ?.map((p) => {
            return p?.projectProductSettings
              ?.map((setting) => setting.productId)
              ?.flat();
          })
          ?.flat()
      ),
    ],
  });

  const { productIds, enabledTools } = transformCtxValues();

  const isPypeEmployee = Boolean(userInfo?.isPypestreamEmployee);

  const projectProducts =
    project?.projectProductSettings?.map(({ productId }) => productId) || [];

  const projectId = isNewProjects
    ? (project as ProjectNew)?.id
    : (project as Project)?.projectId;

  const hasAgentAssistRoles = Boolean(
    projectId
      ? userInfo?.productRoles?.agentAssistRolesSource?.some(
          ({ project: p }) => p?.id === projectId
        )
      : userInfo?.productRoles?.agentAssistRolesSource?.some(
          ({ project: p }) => p?.accountId === selectedOrgId
        )
  );

  let analyticsProjectId;
  const analyticsProjects =
    initialProjects?.filter((p) => {
      return p?.projectProductSettings?.some(
        (s) => s.productId === productIds?.[ProductName.Analytics]
      );
    }) || [];

  if (analyticsProjects.length === 1) {
    analyticsProjectId = isNewProjects
      ? (analyticsProjects[0] as ProjectNew).id
      : (analyticsProjects[0] as Project).projectId;
  }

  const hasAnalyticsRoles = Boolean(
    projectId
      ? userInfo?.productRoles?.analyticsRolesSource?.some(
          ({ project: p }) => p?.id === projectId
        )
      : userInfo?.productRoles?.analyticsRolesSource?.some(
          ({ project: p }) => p?.accountId === selectedOrgId
        )
  );

  let proStudioProjectId;
  const proStudioProjects =
    initialProjects?.filter((p) =>
      p?.projectProductSettings?.some(
        (s) => s.productId === productIds?.[ProductName.ProStudio]
      )
    ) || [];

  if (proStudioProjects.length === 1) {
    proStudioProjectId = isNewProjects
      ? (proStudioProjects[0] as ProjectNew).id
      : (proStudioProjects[0] as Project).projectId;
  }

  const hasProStudioRoles = Boolean(
    projectId
      ? userInfo?.productRoles?.proStudioRolesSource?.some(
          ({ project: p }) => p?.id === projectId
        )
      : userInfo?.productRoles?.proStudioRolesSource?.some(
          ({ project: p }) => p?.accountId === selectedOrgId
        )
  );

  let maestroStudioProjectId;
  const maestroStudioProjects =
    initialProjects?.filter((p) =>
      p?.projectProductSettings?.some(
        (s) => s.productId === productIds?.[ProductName.MaestroStudio]
      )
    ) || [];

  if (maestroStudioProjects.length === 1) {
    maestroStudioProjectId = isNewProjects
      ? (maestroStudioProjects[0] as ProjectNew).id
      : (maestroStudioProjects[0] as Project).projectId;
  }

  const hasMaestroStudioRoles = Boolean(
    projectId
      ? userInfo?.productRoles?.maestroStudioRolesSource?.some(
          ({ project: p }) => p?.id === projectId
        )
      : userInfo?.productRoles?.maestroStudioRolesSource?.some(
          ({ project: p }) => p?.accountId === selectedOrgId
        )
  );

  const commonProductsProps: Omit<Product, 'productId'>[] = [
    {
      name: ProductName.Organization,
      label: 'Manager',
      logo: 'manager',
      url: routes.users,
      disabled: false,
      index: 1,
    },
    {
      name: ProductName.AgentAssist,
      label: 'Contact Center',
      logo: 'contactCenter',
      url:
        hasAgentAssistRoles && projectId
          ? getProductUrl(ProductName.AgentAssist, projectId)
          : undefined,
      conditional: true,
      disabled: !hasAgentAssistRoles,
      index: 2,
    },
    {
      name: ProductName.Analytics,
      label: 'Analytics',
      logo: 'analytics',
      url:
        analyticsProjectId || projectId
          ? getProductUrl(
              ProductName.Analytics,
              analyticsProjectId || projectId
            )
          : undefined,
      conditional: true,
      disabled: !hasAnalyticsRoles,
      index: 3,
    },
    {
      name: ProductName.ProStudio,
      label: 'Pro Studio',
      logo: 'proStudio',
      url:
        proStudioProjectId || projectId
          ? getProductUrl(
              ProductName.ProStudio,
              proStudioProjectId || projectId
            )
          : undefined,
      conditional: true,
      disabled: !hasProStudioRoles,
      index: 4,
    },
    {
      name: ProductName.MaestroStudio,
      label: 'Maestro Studio',
      logo: 'maestro',
      url:
        maestroStudioProjectId || projectId
          ? getProductUrl(
              ProductName.MaestroStudio,
              maestroStudioProjectId || projectId
            )
          : undefined,
      conditional: true,
      disabled: !hasMaestroStudioRoles,
      index: 5,
    },
  ];

  const products: Product[] = commonProductsProps
    .map(({ conditional, disabled, url, name, ...restProps }) => {
      const productId = productIds?.[name] || '';
      const integrated = project ? projectProducts?.includes(productId) : false;
      const isDisabled = !productId
        ? true
        : conditional
          ? disabled ||
            (project ? !integrated : !enabledTools.includes(productId))
          : disabled;

      return {
        ...restProps,
        name,
        url: isDisabled ? undefined : url,
        productId,
        conditional,
        disabled: isDisabled,
        ...(project &&
          conditional && {
            integrated,
          }),
      };
    })
    .sort(
      ({ index: prevIndex }, { index: nextIndex }) =>
        Number(prevIndex) - Number(nextIndex)
    );

  const conditionalProducts: Product[] = products.filter(
    ({ conditional }) => conditional
  );

  const getSelectedProjectURL = () => {
    if (!productIds) return;

    const productUrls = conditionalProducts.filter(({ url }) => url);

    return productUrls.length > 1
      ? undefined
      : productUrls.length === 1
        ? productUrls[0].url
        : products.find(
            ({ productId }) =>
              productId === productIds[ProductName.Organization]
          )?.url;
  };

  const hasAvailableProduct = !project
    ? false
    : project.projectProductSettings?.some((item) =>
        products
          .filter(({ disabled }) => !disabled)
          .some((p) => p.productId === item.productId)
      ) || isPypeEmployee;

  return {
    products,
    conditionalProducts,
    selectedProjectURL: getSelectedProjectURL(),
    hasAvailableProduct,
  };
};
